import { useEffect, useState } from 'react';
import type { FrameRef, RotationKey } from '@/game/assets/manifest';
import { ART_BASE, loadArtManifest } from './uiArt';
import styles from './ItemThumb.module.css';

/**
 * DOM thumbnails of item art (bag, catalog, album), cut straight from the game's atlases with CSS so the UI
 * never touches Pixi. Renders nothing until the art exists: the pastel swatch behind it stays as before.
 */
interface Thumb {
  image: string;
  x: number;
  y: number;
  w: number;
  h: number;
  pageW: number;
  pageH: number;
}

interface SheetJson {
  frames: Record<string, { frame: { x: number; y: number; w: number; h: number } }>;
  meta: { image: string; size: { w: number; h: number } };
}

/** Front view, and the back view for items drawn from four sides (style-guide §10). */
interface Views {
  front: Thumb;
  back?: Thumb;
}

let index: Promise<Map<string, Views>> | undefined;

function loadIndex(base = ART_BASE): Promise<Map<string, Views>> {
  index ??= (async () => {
    const thumbs = new Map<string, Views>();
    try {
      const manifest = await loadArtManifest();
      if (!manifest) return thumbs;
      const frames = new Map<string, Thumb>();
      for (const atlas of manifest.atlases) {
        const sheet = (await (await fetch(`${base}${atlas}`)).json()) as SheetJson;
        for (const [name, f] of Object.entries(sheet.frames))
          frames.set(name, {
            image: `${base}${sheet.meta.image}`,
            ...f.frame,
            pageW: sheet.meta.size.w,
            pageH: sheet.meta.size.h,
          });
      }
      for (const [id, art] of Object.entries(manifest.items)) {
        const r0: FrameRef | undefined = art.frames.r0;
        const r180: FrameRef | undefined = art.frames.r180;
        const front = typeof r0 === 'string' ? frames.get(r0) : undefined;
        const back = typeof r180 === 'string' ? frames.get(r180) : undefined;
        if (front) thumbs.set(id, back ? { front, back } : { front });
      }
    } catch {
      // No art yet (or offline before first load): swatches stay.
    }
    return thumbs;
  })();
  return index;
}

/**
 * `view` turns the item like in the room (r90 = mirrored front, r180 = back, r270 = mirrored back); items
 * drawn from one side only show their front, mirrored where the room mirrors it.
 */
export function ItemThumb({ itemId, view = 'r0' }: { itemId: string; view?: RotationKey }) {
  const [views, setViews] = useState<Views | null>(null);
  useEffect(() => {
    let live = true;
    void loadIndex().then((m) => {
      if (live) setViews(m.get(itemId) ?? null);
    });
    return () => {
      live = false;
    };
  }, [itemId]);
  if (!views) return null;
  const thumb = view === 'r180' || view === 'r270' ? (views.back ?? views.front) : views.front;
  const mirror = view === 'r90' || view === 'r270';
  const { image, x, y, w, h, pageW, pageH } = thumb;
  return (
    <span className={styles.box} aria-hidden="true">
      <span
        className={styles.sprite}
        style={{
          aspectRatio: `${String(w)} / ${String(h)}`,
          ['--ratio' as string]: String(w / h),
          backgroundImage: `url(${image})`,
          backgroundSize: `${String((pageW / w) * 100)}% ${String((pageH / h) * 100)}%`,
          backgroundPosition: `${String(pageW === w ? 0 : (x / (pageW - w)) * 100)}% ${String(pageH === h ? 0 : (y / (pageH - h)) * 100)}%`,
          ...(mirror ? { transform: 'scaleX(-1)' } : {}),
        }}
      />
    </span>
  );
}
